import { DownOutlined } from '@ant-design/icons';
import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { FC } from 'react';

import '../styles/LeadMessageTranscription.scss';

interface LeadMessageTranscriptionProps {
  text: string | null;
}

// Расшифровка речи: у записи звонка и у голосового сообщения она выглядит и
// ведёт себя одинаково, поэтому живёт отдельным компонентом рядом с AiGrade.
export const LeadMessageTranscription: FC<LeadMessageTranscriptionProps> = ({
  text,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);

  const observerRef = useRef<ResizeObserver | null>(null);
  const isExpandedRef = useRef(false);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  // Кнопка «Показать полностью» нужна только расшифровкам, которые не
  // помещаются в свёрнутые (line-clamp) две строки. Раскрытое состояние не
  // измеряем: там clamp снят и scrollHeight === clientHeight всегда.
  const textRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node) {
      setIsExpandable(false);
      return;
    }

    const updateExpandable = () => {
      if (isExpandedRef.current) return;
      setIsExpandable(node.scrollHeight > node.clientHeight);
    };

    updateExpandable();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateExpandable);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
    },
    []
  );

  // Пустая строка так же «нечего показывать», как и null: иначе останется
  // висеть пунктирный разделитель над пустотой.
  if (!text) return null;

  return (
    <div className="LeadMessageTranscription">
      <div
        ref={textRef}
        className={cn('LeadMessageTranscription__text', {
          'LeadMessageTranscription__text--expanded': isExpanded,
        })}
      >
        {text}
      </div>
      {isExpandable && (
        <button
          type="button"
          className="LeadMessageTranscription__toggle"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? 'Свернуть' : 'Показать полностью'}
          <DownOutlined
            className={cn('LeadMessageTranscription__toggle-icon', {
              'LeadMessageTranscription__toggle-icon--expanded': isExpanded,
            })}
          />
        </button>
      )}
    </div>
  );
};
